// The hub's WebSocket to the cloud (ops plan 2026-10-16-command-latency, "Design: the hub's WebSocket to the cloud";
// Andrew, 2026-10-17: build it, speed over data cost). One long-lived socket per hub carries liveness (the cloud's
// protocol ping every NODE_SOCKET_PING_MS, the hub's pong), what the held poll hands over (with no wait), and the units'
// answers up at once. The held poll (node-poll.ts) stays as the fallback when the socket can't connect.
//
// The handshake: on upgrade the cloud sends `challenge` with a fresh nonce; the hub answers `hello`, a SignedRequest over
// a NodeSocketHello naming that nonce, signed with the node's identity key exactly as a poll is (domain-separated by
// `kind`). Until the hello verifies the cloud reads nothing else and sends nothing else. Every frame is JSON with `t`
// and `v`; a receiver ignores a frame whose `t` it doesn't know, so frames can be added later.
import { z } from 'zod';
import { DeviceCommandResult } from './protocol.ts';
import { NodeDiagnosticCommand, NodeLabAnswer, RelayedDeviceCommand } from './node-poll.ts';

/** The socket's path on the cloud API, and its subprotocol. */
export const NODE_SOCKET_PATH = '/node/v1/socket';
export const NODE_SOCKET_PROTOCOL = 'radish-node.v1';

/** The cloud pings each socket this often; it ends one that misses NODE_SOCKET_MISSED_PONGS pongs in a row. */
export const NODE_SOCKET_PING_MS = 5_000;
export const NODE_SOCKET_MISSED_PONGS = 2;
/** The hub must say a verified hello this soon after the upgrade. */
export const NODE_SOCKET_HELLO_MS = 5_000;
/**
 * The largest frame the hub accepts from the cloud (a hand-over of up to 200 commands), and the largest the cloud
 * accepts from a hub (a results frame; the agent splits larger ones), which is also all an unverified socket can make
 * the cloud buffer. A hello over NODE_SOCKET_MAX_HELLO is refused.
 */
export const NODE_SOCKET_MAX_FRAME = 256 * 1024;
export const NODE_SOCKET_MAX_HUB_FRAME = 64 * 1024;
export const NODE_SOCKET_MAX_HELLO = 4 * 1024;
/** On a deploy the cloud asks each hub to wait up to this long before reconnecting, so a fleet doesn't come back at once. */
export const NODE_SOCKET_BYE_SPREAD_MS = 5_000;

/** Why the cloud closed a socket (4000–4999 are the application's own). */
export const NODE_SOCKET_CLOSE = {
  /** A newer socket for the same node replaced this one. */
  replaced: 4000,
  /** The hello was malformed, unsigned, for an unknown node, stale, or named another nonce. */
  unauthorized: 4401,
  /** No verified hello within NODE_SOCKET_HELLO_MS. */
  helloTimeout: 4408,
  /** The node is not claimed: it says hello over HTTP, it does not hold a socket. */
  unclaimed: 4409,
  /** The cloud hasn't turned the socket on for this hub (RADISH_NODE_SOCKET_HUBS): it goes back to the held poll. */
  notEnabled: 4403,
  /** The hub's sends outran the socket (more than NODE_SOCKET_MAX_FRAME buffered); it reconnects and is resent. */
  backpressure: 1013,
  /** The cloud is restarting (a deploy): reconnect after the bye's `reconnectAfterMs`. */
  restart: 1012,
} as const;

/** What the hub signs in its hello. */
export const NodeSocketHello = z.strictObject({
  kind: z.literal('socket-hello'),
  nodeId: z.uuid(),
  serial: z.string().max(64),
  sentAt: z.int().positive(),
  /** The cloud's challenge nonce, echoed: a hello is good for one socket only. */
  nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  release: z.string().max(64),
  /** What the agent keeps from a hand-over, as a poll's `commandReads`. */
  commandReads: z.array(z.string().max(32)).max(8),
  /** A lab node's entered conditions (as a poll's); absent and the cloud never sends `lab`. */
  labConditionIds: z.array(z.uuid()).max(16).optional(),
});
export type NodeSocketHello = z.infer<typeof NodeSocketHello>;

const frame = <T extends string, S extends z.ZodRawShape>(t: T, shape: S) => z.object({ t: z.literal(t), v: z.literal(1), ...shape });

/** The units' command answers, as a poll's `results`. */
const UnitResults = z.array(DeviceCommandResult.extend({ replica: z.string().max(64) })).max(50);

// Cloud to hub.
export const SocketChallenge = frame('challenge', { nonce: z.string().regex(/^[A-Za-z0-9_-]{43}$/), serverTime: z.int() });
export const SocketReady = frame('ready', { sessionId: z.uuid(), pingMs: z.int().positive(), serverTime: z.int() });
/** What waits for the node, the moment it is written: a poll answer's parts. `id` is echoed in the hub's `ack`. */
export const SocketDeliver = frame('deliver', {
  id: z.uuid(),
  deviceCommands: z.array(RelayedDeviceCommand).max(200),
  deviceAlerts: z.array(RelayedDeviceCommand).max(200).optional(),
  labCommands: z.array(RelayedDeviceCommand).max(200).optional(),
  screenCommands: z.array(RelayedDeviceCommand).max(200).optional(),
  logCommands: z.array(RelayedDeviceCommand).max(200).optional(),
  lab: NodeLabAnswer.optional(),
  diagnostics: z.array(NodeDiagnosticCommand).max(5).optional(),
});
/** The cloud recorded a `results` frame (its `id`). */
export const SocketRecorded = frame('recorded', { id: z.uuid(), recordedAt: z.iso.datetime() });
/** Something new for the hub's upstream pull: pull now. */
export const SocketPull = frame('pull', {});
/** The cloud is going away: reconnect after this long. */
export const SocketBye = frame('bye', { reconnectAfterMs: z.int().min(0).max(60_000) });

// Hub to cloud.
export const SocketHelloFrame = frame('hello', { payload: z.string().min(2).max(NODE_SOCKET_MAX_HELLO), signature: z.string().min(16).max(256) });
/** The units' answers, up at once; `labConditionIds` updates the lab node's entered set. */
export const SocketResults = frame('results', { id: z.uuid(), results: UnitResults, labConditionIds: z.array(z.uuid()).max(16).optional() });
/** The hub kept a `deliver` (its `id`). */
export const SocketAck = frame('ack', { id: z.uuid() });

export const CloudSocketFrame = z.discriminatedUnion('t', [SocketChallenge, SocketReady, SocketDeliver, SocketRecorded, SocketPull, SocketBye]);
export type CloudSocketFrame = z.infer<typeof CloudSocketFrame>;
export const HubSocketFrame = z.discriminatedUnion('t', [SocketHelloFrame, SocketResults, SocketAck]);
export type HubSocketFrame = z.infer<typeof HubSocketFrame>;

/**
 * A received frame: its parse, `unknown` for a well-formed frame of a kind this side doesn't know (ignored, so frames
 * can be added), or `malformed`.
 */
export function readSocketFrame<T>(schema: z.ZodType<T>, known: readonly string[], text: string): { readonly frame: T } | 'unknown' | 'malformed' {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return 'malformed';
  }
  const t = typeof json === 'object' && json !== null ? (json as { t?: unknown }).t : undefined;
  if (typeof t !== 'string') return 'malformed';
  if (!known.includes(t)) return 'unknown';
  const parsed = schema.safeParse(json);
  return parsed.success ? { frame: parsed.data } : 'malformed';
}

export const CLOUD_FRAME_KINDS = ['challenge', 'ready', 'deliver', 'recorded', 'pull', 'bye'] as const;
export const HUB_FRAME_KINDS = ['hello', 'results', 'ack'] as const;
